"use client";

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  History,
  RotateCcw,
  Clock,
  ChevronDown,
  ChevronUp,
  User,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { listProposalVersions, restoreProposalVersion } from "@/lib/api/proposals";
import { getErrorMessage } from "@/lib/api/client";
import { formatDate } from "@/lib/utils";
import type { ProposalVersion } from "@/types";

interface VersionHistoryPanelProps {
  proposalId: number;
  currentVersionId: number | null;
  onVersionRestored?: (version: ProposalVersion) => void;
  readOnly?: boolean;
}

export function VersionHistoryPanel({
  proposalId,
  currentVersionId,
  onVersionRestored,
  readOnly = false,
}: VersionHistoryPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<ProposalVersion | null>(null);
  
  const queryClient = useQueryClient();

  const { data: versionsData, isLoading } = useQuery({
    queryKey: ["proposal-versions", proposalId],
    queryFn: () => listProposalVersions(proposalId, { page_size: 50 }),
    enabled: isOpen,
  });

  const restoreMutation = useMutation({
    mutationFn: (versionId: number) => restoreProposalVersion(proposalId, versionId),
    onSuccess: (data) => {
      toast.success(`Version restaurée (v${data.current_version?.version_number})`);
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      queryClient.invalidateQueries({ queryKey: ["proposal-versions", proposalId] });
      setRestoreDialogOpen(false);
      setVersionToRestore(null);
      if (onVersionRestored && data.current_version) {
        onVersionRestored(data.current_version);
      }
    },
    onError: (error) => {
      toast.error("Erreur lors de la restauration", {
        description: getErrorMessage(error),
      });
    },
  });

  const handleRestoreClick = (version: ProposalVersion) => {
    setVersionToRestore(version);
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = () => {
    if (versionToRestore) {
      restoreMutation.mutate(versionToRestore.id);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="gap-2"
      >
        <History className="h-4 w-4" />
        Historique
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Historique des versions
            </DialogTitle>
            <DialogDescription>
              Consultez et restaurez les versions précédentes de votre proposition.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[400px] pr-4">
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : versionsData?.items.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucune version disponible
              </p>
            ) : (
              <div className="space-y-2">
                {versionsData?.items.map((version) => (
                  <VersionItem
                    key={version.id}
                    version={version}
                    isCurrent={version.id === currentVersionId}
                    onRestore={() => handleRestoreClick(version)}
                    readOnly={readOnly}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restaurer la version {versionToRestore?.version_number} ?</DialogTitle>
            <DialogDescription>
              Une nouvelle version sera créée avec le contenu de la version {versionToRestore?.version_number}.
              L&apos;historique actuel sera conservé.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRestoreDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={handleRestoreConfirm}
              disabled={restoreMutation.isPending}
            >
              {restoreMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Restaurer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface VersionItemProps {
  version: ProposalVersion;
  isCurrent: boolean;
  onRestore: () => void;
  readOnly?: boolean;
}

function VersionItem({ version, isCurrent, onRestore, readOnly }: VersionItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className={`border rounded-lg p-3 ${
        isCurrent ? "border-primary bg-primary/5" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">Version {version.version_number}</span>
            {isCurrent && (
              <Badge variant="default" className="text-xs">
                Actuelle
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
            <Clock className="h-3 w-3" />
            {formatDate(version.created_at)}
          </div>
          {version.notes && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {version.notes}
            </p>
          )}
        </div>
        
        {!isCurrent && !readOnly && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRestore}
            title="Restaurer cette version"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Expandable details */}
      <button
        className="flex items-center gap-1 text-xs text-muted-foreground mt-2 hover:text-foreground"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )}
        Détails
      </button>

      {isExpanded && (
        <div className="mt-2 pt-2 border-t text-xs space-y-1">
          <p>
            <span className="text-muted-foreground">ID:</span> {version.id}
          </p>
          <p>
            <span className="text-muted-foreground">Éléments:</span>{" "}
            {version.canvas_data?.objects?.length || 0}
          </p>
          {version.editable_values && Object.keys(version.editable_values).length > 0 && (
            <p>
              <span className="text-muted-foreground">Valeurs modifiées:</span>{" "}
              {Object.keys(version.editable_values).length}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
